import React, { Component, PropsWithChildren, Fragment, ReactElement } from 'react';
import { CardGridContext, ICardGridCommonRenderProps } from './CardGrid';
import { DataGridContext, IDataGridCommonRenderProps } from './DataGrid';
import { GridControlProps, GridControlType } from './types';

/**
 * Grid control is for single selection grid actions
 * If nothing is selected, the actions are disabled
 * If multi select is active, the actions are not rendered
 * as they will be in the grid panel
 */
export class GridControl<T> extends Component<GridControlProps<T>, {}> {
  public render() {
    const { type = 'datagrid' } = this.props;
    return (
      <Fragment>
        {type === 'datagrid' ? (
          <DataGridContext.Consumer>{(renderProps) => this.renderWithContext(renderProps)}</DataGridContext.Consumer>
        ) : (
          <CardGridContext.Consumer>{(renderProps) => this.renderWithContext(renderProps)}</CardGridContext.Consumer>
        )}
      </Fragment>
    );
  }

  private renderWithContext = (context: IDataGridCommonRenderProps<T> | ICardGridCommonRenderProps<T>) => {
    const { enabledCondition = '1', hideOnMultiSelect = true, type = 'datagrid' } = this.props;
    let data: (string | number)[] | T[] = [];
    if (isDataGridControl<T>(context, type)) {
      data = context.rowData.multi;
    }

    const singleSelection = data.length === 1;
    const multiSelection = data.length > 1;
    const atLeastOneSelection = data.length;
    const rendered = isCardGridControl<T>(context, type)
      ? (this.props.render as (props: ICardGridCommonRenderProps<T>) => ReactElement<{}> | null)(context)
      : (this.props.render as (props: IDataGridCommonRenderProps<T>) => ReactElement<{}> | null)(context);
    const shouldDisable =
      rendered &&
      ((!singleSelection && enabledCondition === '1') ||
        (!multiSelection && enabledCondition === '>1') ||
        (!atLeastOneSelection && enabledCondition === '>=1'));

    let Control: JSX.Element | null = context.multiSelectActive && hideOnMultiSelect ? null : rendered;

    if (Control && shouldDisable) {
      Control = React.cloneElement(rendered as ReactElement<any>, {
        disabled: true,
        href: null,
      });
    }

    return Control;
  };
}

function isCardGridControl<T>(
  context: IDataGridCommonRenderProps<T> | ICardGridCommonRenderProps<T>,
  type: GridControlType,
): context is ICardGridCommonRenderProps<T> {
  return type === 'cardgrid';
}

function isDataGridControl<T>(
  context: IDataGridCommonRenderProps<T> | ICardGridCommonRenderProps<T>,
  type: GridControlType,
): context is IDataGridCommonRenderProps<T> {
  return type === 'datagrid';
}

export default GridControl;
